import type { ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { Button, DataTable, Drawer, EmptyView, StatusDot, Tag } from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useWhatsappQuery } from "../services/useWhatsapp";
import {
  languageDisplayName,
  providerLabel,
  templateDisplayName,
} from "../services/value-format";
import type {
  WhatsappMetaHistoryEvent,
  WhatsappMetaHistoryItem,
} from "../types/whatsapp.types";
import { NoRecords, QueryState, Timestamp } from "./Shared";
import { kvStyle, listStyle, muted, rowButton, sectionHeading, summaryStyle } from "./styles";

const copy = keys.whatsapp;

function latestEvent(item: WhatsappMetaHistoryItem) {
  return item.events.reduce<WhatsappMetaHistoryEvent | undefined>(
    (latest, event) => {
      if (!event.timestamp) return latest;
      if (!latest?.timestamp) return event;
      return Date.parse(event.timestamp) >= Date.parse(latest.timestamp)
        ? event
        : latest;
    },
    undefined,
  );
}

export function HistoryStatus({
  status,
  providerStatus,
}: {
  status?: string;
  providerStatus?: string | null;
}) {
  const t = useTranslations();
  const normalized = status?.toLowerCase();
  const label =
    normalized && Object.hasOwn(copy.states, normalized)
      ? t((copy.states as Record<string, string>)[normalized])
      : status || t(copy.notAvailable);
  return (
    <StatusDot
      tone={
        normalized === "failed"
          ? "red"
          : normalized === "delivered" || normalized === "read"
            ? "green"
            : normalized === "sent" || normalized === "accepted"
              ? "blue"
              : normalized === "unknown"
                ? "amber"
                : "grey"
      }
    >
      <bdi>
        {label}
        {normalized === "unknown" &&
        providerStatus &&
        providerStatus.toLowerCase() !== "unknown"
          ? ` (${providerStatus})`
          : ""}
      </bdi>
    </StatusDot>
  );
}

export function Detail({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    // `display: contents` keeps dt + dd as direct cells of the `rd-kv` grid.
    <div style={{ display: "contents" }}>
      <dt>{label}</dt>
      <dd style={{ overflowWrap: "anywhere" }}>{children}</dd>
    </div>
  );
}

export function EventDetails({ event }: { event: WhatsappMetaHistoryEvent }) {
  const t = useTranslations();
  return (
    <li
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minWidth: 0,
        padding: "12px 14px",
        borderRadius: "var(--rd-radius-card)",
        boxShadow: "var(--rd-shadow-card)",
      }}
    >
      <div>
        <HistoryStatus
          status={event.status}
          providerStatus={event.providerStatus}
        />
      </div>
      <dl className="rd-kv" style={kvStyle}>
        <Detail label={t(copy.metaEventTime)}>
          <Timestamp value={event.timestamp} />
        </Detail>
        <Detail label={t(copy.historyReceivedAt)}>
          <Timestamp value={event.receivedAt} />
        </Detail>
      </dl>
      {(event.errorCode || event.errors.length > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, color: "var(--rd-red-tag-ink)", background: "var(--rd-red-tag)", padding: "8px 12px", borderRadius: "var(--rd-radius-lg)" }}>
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
            {t(copy.historyFailure)}
          </h4>
          {event.errorCode && (
            <p style={{ margin: 0 }}>
              {t(copy.errorCode)}: <bdi>{event.errorCode}</bdi>
            </p>
          )}
          {event.errors.map((error, index) => (
            <div
              key={index}
              style={{ display: "flex", flexDirection: "column", gap: 4, overflowWrap: "anywhere" }}
            >
              {error.code && error.code !== event.errorCode && (
                <p style={{ margin: 0 }}>
                  {t(copy.errorCode)}: <bdi>{error.code}</bdi>
                </p>
              )}
              {[...new Set([error.title, error.message, error.details])]
                .filter(Boolean)
                .map((text) => (
                  <p key={text} style={{ margin: 0 }}>
                    <bdi>{text}</bdi>
                  </p>
                ))}
            </div>
          ))}
        </div>
      )}
      <details>
        <summary style={summaryStyle}>
          {t(copy.historyPricing)}
        </summary>
        <p style={{ ...muted, margin: "8px 0" }}>{t(copy.historyPricingHint)}</p>
        <dl className="rd-kv" style={kvStyle}>
          <Detail label={t(copy.historyBillable)}>
            {t(
              event.billable === true
                ? copy.billable
                : event.billable === false
                  ? copy.free
                  : copy.billingUnknown,
            )}
          </Detail>
          <Detail label={t(copy.pricingModel)}>
            <bdi>{event.pricingModel ?? t(copy.notAvailable)}</bdi>
          </Detail>
          <Detail label={t(copy.pricingType)}>
            <bdi>
              {event.pricingType
                ? providerLabel(event.pricingType, t)
                : t(copy.notAvailable)}
            </bdi>
          </Detail>
          <Detail label={t(copy.pricingCategory)}>
            <bdi>
              {event.pricingCategory
                ? providerLabel(event.pricingCategory, t)
                : t(copy.notAvailable)}
            </bdi>
          </Detail>
        </dl>
      </details>
      {(event.conversationId ||
        event.conversationOrigin ||
        event.conversationExpiresAt) && (
        <details>
          <summary style={summaryStyle}>
            {t(copy.historyConversation)}
          </summary>
          <dl className="rd-kv" style={{ ...kvStyle, marginTop: 10 }}>
            <Detail label={t(copy.historyConversationId)}>
              <bdi dir="ltr">
                {event.conversationId ?? t(copy.notAvailable)}
              </bdi>
            </Detail>
            <Detail label={t(copy.historyConversationOrigin)}>
              <bdi>
                {event.conversationOrigin
                  ? providerLabel(event.conversationOrigin, t)
                  : t(copy.notAvailable)}
              </bdi>
            </Detail>
            <Detail label={t(copy.historyConversationExpires)}>
              <Timestamp value={event.conversationExpiresAt} />
            </Detail>
          </dl>
        </details>
      )}
    </li>
  );
}

export function MessagesPanel({
  query,
  messageId,
  onMessage,
  onNext,
  onPrevious,
  onRefresh,
  hasPrevious,
}: {
  query: string;
  messageId: string | null;
  onMessage: (id: string | null) => void;
  onNext: (after: string) => void;
  onPrevious: () => void;
  onRefresh: () => void;
  hasPrevious: boolean;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const result = useWhatsappQuery("history", query);
  const data = result.data;
  const selected = data?.items.find((item) => item.id === messageId);
  const selectedEvents = selected
    ? [...selected.events].sort((a, b) => {
        if (!a.timestamp) return b.timestamp ? 1 : 0;
        if (!b.timestamp) return -1;
        return Date.parse(a.timestamp) - Date.parse(b.timestamp);
      })
    : [];
  const messageName = (item: WhatsappMetaHistoryItem) => {
    if (item.context?.templateName)
      return templateDisplayName(item.context.templateName, t);
    const purpose = item.context?.purpose;
    return purpose && Object.hasOwn(copy.purposes, purpose)
      ? t(copy.purposes[purpose])
      : t(copy.historyMessageUnknown);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <Tag tone="grey">{t(copy.webhookSource)}</Tag>
        <Button variant="secondary" size="sm" icon={RefreshCw} loading={result.isFetching} loadingLabel={t("whatsapp.refreshing")} onClick={onRefresh}>
          {t(copy.refresh)}
        </Button>
      </div>
      <p style={muted}>{t(copy.metaHistoryHint)}</p>
      <QueryState loading={result.isLoading} error={result.error} retry={result.refetch} page={t(copy.metaUpdates)} rows={6}>
        {data && (
          <>
            <p style={muted}>
              {data.coverage.recordedSince
                ? t(copy.webhookCoverage, {
                    since: new Date(data.coverage.recordedSince).toLocaleDateString(locale, { timeZone: "UTC" }),
                    days: data.coverage.retentionDays,
                  })
                : t(copy.webhookRetention, { days: data.coverage.retentionDays })}
            </p>
            {data.items.length ? (
              <DataTable
                label={t(copy.messages)}
                rows={data.items}
                rowKey={(item) => item.id}
                minWidth={640}
                empty={null}
                onRowClick={(item) => onMessage(item.id)}
                footer={
                  data.items.length > 0 || hasPrevious || data.after ? (
                    <nav className="rd-table-foot" aria-label={t(copy.pagination)}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!hasPrevious || result.isFetching}
                        onClick={onPrevious}
                      >
                        {t(copy.previous)}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!data.after || result.isFetching}
                        onClick={() => {
                          if (data.after) onNext(data.after);
                        }}
                      >
                        {t(copy.next)}
                      </Button>
                    </nav>
                  ) : undefined
                }
                columns={[
                  {
                    key: "message",
                    label: t(copy.purpose),
                    width: 2,
                    render: (item) => (
                      <button
                        type="button"
                        className="rd-cell-stack"
                        onClick={(event) => {
                          event.stopPropagation();
                          onMessage(item.id);
                        }}
                        aria-label={t(copy.viewMessageNamed, {
                          name: `${messageName(item)} · ${item.recipientMasked ?? t(copy.notAvailable)}`,
                        })}
                        style={rowButton}
                      >
                        <span className="rd-item-name">
                          <bdi>{messageName(item)}</bdi>
                        </span>
                        <span className="rd-cell-sub">
                          {t(copy.recipient)}: <bdi dir="ltr">{item.recipientMasked ?? t(copy.notAvailable)}</bdi>
                        </span>
                      </button>
                    ),
                  },
                  {
                    key: "status",
                    label: t(copy.historyLatestUpdate),
                    render: (item) => (
                      <HistoryStatus
                        status={latestEvent(item)?.status}
                        providerStatus={latestEvent(item)?.providerStatus}
                      />
                    ),
                  },
                  {
                    key: "time",
                    label: t(copy.metaEventTime),
                    render: (item) => (
                      <span className="rd-num" style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>
                        <Timestamp value={latestEvent(item)?.timestamp ?? null} />
                      </span>
                    ),
                  },
                ]}
              />
            ) : (
              <>
                <div className="rd-card">
                  <NoRecords
                    title={t("whatsapp.metaHistoryEmptyTitle")}
                    text={t(copy.metaHistoryEmpty)}
                    matchTitle={t(copy.metaHistoryCheckedEmpty)}
                    forceMatch={Boolean(data.coverage.recordedSince)}
                  />
                </div>
                {hasPrevious || data.after ? (
                  <nav style={{ display: "flex", gap: 8 }} aria-label={t(copy.pagination)}>
                    <Button variant="secondary" size="sm" disabled={!hasPrevious || result.isFetching} onClick={onPrevious}>
                      {t(copy.previous)}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!data.after || result.isFetching}
                      onClick={() => {
                        if (data.after) onNext(data.after);
                      }}
                    >
                      {t(copy.next)}
                    </Button>
                  </nav>
                ) : null}
              </>
            )}
            {data.eventsTruncated && <p style={muted}>{t(copy.metaHistoryPageTruncated)}</p>}
          </>
        )}
      </QueryState>
      <Drawer
        open={Boolean(messageId)}
        title={selected ? messageName(selected) : t(copy.details)}
        sub={
          selected ? (
            <>
              {t(copy.recipient)}: <bdi dir="ltr">{selected.recipientMasked ?? t(copy.notAvailable)}</bdi>
            </>
          ) : undefined
        }
        crumb={`${t(copy.nav)} / ${t(copy.details)}`}
        badge="chat"
        width={540}
        closeLabel={t(copy.close)}
        onClose={() => onMessage(null)}
      >
        <QueryState loading={result.isLoading} error={result.error} retry={result.refetch} page={t(copy.details).toLocaleLowerCase(locale)}>
          {selected ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
              <h3 style={sectionHeading}>{t(copy.timeline)}</h3>
                {selected.events.length ? (
                  <ol style={listStyle}>
                    {selectedEvents.map((event, index) => (
                      <EventDetails
                        key={`${index}-${event.status}-${event.timestamp}`}
                        event={event}
                      />
                    ))}
                  </ol>
                ) : (
                  <p style={muted}>{t(copy.metaHistoryNoEvents)}</p>
                )}
                {selected.eventsTruncated && (
                  <p style={muted}>{t(copy.metaHistoryTruncated)}</p>
                )}
                <details>
                  <summary style={summaryStyle}>
                    {t(copy.historyIdentifiers)}
                  </summary>
                  <dl className="rd-kv" style={{ ...kvStyle, marginTop: 10 }}>
                    <Detail label={t(copy.providerMessageId)}>
                      <bdi dir="ltr">{selected.messageId}</bdi>
                    </Detail>
                    <Detail label={t(copy.sender)}>
                      <bdi dir="ltr">{selected.phoneNumberId}</bdi>
                    </Detail>
                    <Detail label={t(copy.waba)}>
                      <bdi dir="ltr">
                        {selected.businessAccountId ?? t(copy.notAvailable)}
                      </bdi>
                    </Detail>
                  </dl>
                </details>
                <details>
                  <summary style={summaryStyle}>
                    {t(copy.historyContext)}
                  </summary>
                  {selected.context ? (
                    <>
                      <p style={{ ...muted, margin: "8px 0" }}>{t(copy.historyContextHint)}</p>
                      <dl className="rd-kv" style={kvStyle}>
                        <Detail label={t(copy.store)}>
                          <bdi>
                            {selected.context.storeName ?? t(copy.notAvailable)}
                          </bdi>
                        </Detail>
                        <Detail label={t(copy.seller)}>
                          <bdi>
                            {selected.context.sellerName ??
                              t(copy.notAvailable)}
                          </bdi>
                        </Detail>
                        <Detail label={t(copy.storeId)}>
                          <bdi dir="ltr">
                            {selected.context.storeId ?? t(copy.notAvailable)}
                          </bdi>
                        </Detail>
                        <Detail label={t(copy.sellerId)}>
                          <bdi dir="ltr">
                            {selected.context.sellerId ?? t(copy.notAvailable)}
                          </bdi>
                        </Detail>
                        <Detail label={t(copy.template)}>
                          <bdi>{selected.context.templateName}</bdi>
                        </Detail>
                        <Detail label={t(copy.purpose)}>
                          <bdi>
                            {Object.hasOwn(
                              copy.purposes,
                              selected.context.purpose,
                            )
                              ? t(copy.purposes[selected.context.purpose])
                              : selected.context.purpose}
                          </bdi>
                        </Detail>
                        <Detail label={t(copy.language)}>
                          <bdi>
                            {languageDisplayName(
                              selected.context.languageCode,
                              locale,
                            )}{" "}
                            ({selected.context.languageCode})
                          </bdi>
                        </Detail>
                        <Detail label={t(copy.historyRequestResult)}>
                          <HistoryStatus status={selected.context.acceptance} />
                        </Detail>
                        <Detail label={t(copy.attemptedAt)}>
                          <Timestamp value={selected.context.attemptedAt} />
                        </Detail>
                        <Detail label={t(copy.historyAcceptedAt)}>
                          <Timestamp value={selected.context.acceptedAt} />
                        </Detail>
                        <Detail label={t(copy.historyAttemptId)}>
                          <bdi dir="ltr">{selected.context.attemptId}</bdi>
                        </Detail>
                      </dl>
                    </>
                  ) : (
                    <p style={{ ...muted, marginTop: 8 }}>{t(copy.historyContextMissing)}</p>
                  )}
                </details>
              </div>
            ) : (
              data && <EmptyView title={t("whatsapp.metaHistoryMissingTitle")} sub={t(copy.metaHistoryMissing)} />
            )}
          </QueryState>
      </Drawer>
    </div>
  );
}

